import { Editor } from "@tinymce/tinymce-react";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

export const TinyMCEEditorField = forwardRef(
  (
    {
      label,
      content,
      handleEditorChange,
      required = false,
      showError = true,
      id,
      maxLength,
      minLength,
      showCharCount = false,
      height = 500,
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [hasInteracted, setHasInteracted] = useState(false);
    const editorRef = useRef(null);
    const [internalContent, setInternalContent] = useState(content || "");

    // Function to strip HTML tags and check if content is empty
    const getTextContent = useCallback((htmlContent) => {
      if (!htmlContent) return "";

      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        return textContent.trim();
      } catch (error) {
        return htmlContent.replace(/<[^>]*>/g, "").trim();
      }
    }, []);

    const validateContent = useCallback(
      (htmlContent) => {
        const contentToValidate =
          htmlContent !== undefined ? htmlContent : internalContent;
        const textContent = getTextContent(contentToValidate);

        if (required && (!textContent || textContent.length === 0)) {
          return "This field is required";
        }

        if (minLength && textContent.length < minLength) {
          return `Minimum ${minLength} characters required`;
        }

        if (maxLength && textContent.length > maxLength) {
          return `Maximum ${maxLength} characters allowed`;
        }

        return "";
      },
      [required, minLength, maxLength, getTextContent, internalContent]
    );

    // ✅ Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          const validationError = validateContent(internalContent);
          setError(validationError);
          return validationError;
        },
        touch: () => {
          setHasInteracted(true);
        },
        getValue: () => internalContent,
        getError: () => error,
        isRequired: () => required,
        reset: () => {
          setInternalContent("");
          setError("");
          setHasInteracted(false);
          if (editorRef.current) {
            editorRef.current.setContent("");
          }
        },
      }),
      [validateContent, internalContent, error, required]
    );

    const handleChange = (newContent, editor) => {
      setInternalContent(newContent);

      if (handleEditorChange && typeof handleEditorChange === "function") {
        handleEditorChange(newContent, editor);
      }

      if (!hasInteracted) {
        setHasInteracted(true);
      }

      const validationError = validateContent(newContent);
      setError(validationError);
    };

    const handleBlur = useCallback(() => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      const currentContent = editorRef.current
        ? editorRef.current.getContent()
        : internalContent;
      setInternalContent(currentContent);

      const validationError = validateContent(currentContent);
      setError(validationError);
    }, [hasInteracted, validateContent, internalContent]);

    const handleKeyUp = useCallback(() => {
      if (hasInteracted && editorRef.current) {
        const currentContent = editorRef.current.getContent();
        setInternalContent(currentContent);

        const validationError = validateContent(currentContent);
        setError(validationError);
      }
    }, [hasInteracted, validateContent]);

    const handlePaste = useCallback(() => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      setTimeout(() => {
        if (editorRef.current) {
          const currentContent = editorRef.current.getContent();
          setInternalContent(currentContent);

          const validationError = validateContent(currentContent);
          setError(validationError);
        }
      }, 300);
    }, [hasInteracted, validateContent]);

    const handleFocus = useCallback(() => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
    }, [hasInteracted]);

    useEffect(() => {
      setInternalContent(content || "");
    }, [content]);

    useEffect(() => {
      if (hasInteracted) {
        const validationError = validateContent(internalContent);
        setError(validationError);
      }
    }, [internalContent, hasInteracted, validateContent]);

    // Get text content for character counting
    const textContent = getTextContent(internalContent);
    const charCount = textContent.length;
    const isOverLimit = maxLength && charCount > maxLength;

    return (
      <div className="mb-3">
        <label className="form-label">
          {label} {required ? <span className="required-field">*</span> : ""}
        </label>

        <div
          className={`tinymce-wrapper ${
            (error && hasInteracted ? "is-invalid" : "") +
            (required ? " required" : "")
          }`}
        >
          <Editor
            id={id}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            value={internalContent}
            init={{
              height: height,
              branding: false,
              promotion: false,
              
              plugins: [
                "advlist",
                "autolink",
                "link",
                "image",
                "lists",
                "charmap",
                "preview",
                "anchor",
                "pagebreak",
                "searchreplace",
                "wordcount",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "media",
                "table",
                "emoticons",
                "template",
                "help",
              ],
              toolbar:
                "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | preview media fullscreen | emoticons | help",
              content_style:
                "body { font-family: 'Inter', sans-serif !important; font-size: 14px; }",
              menubar: "file edit view insert format tools table help",
              // Custom color palette (optional)
              setup: (editor) => {
                editorRef.current = editor;
                editor.on("blur", handleBlur);
                editor.on("focus", handleFocus);
                editor.on("keyup", handleKeyUp);
                editor.on("paste", handlePaste);
              },
            }}
            onEditorChange={handleChange}
          />
        </div>

        {/* Character counter */}
        {showCharCount && maxLength && (
          <div
            className={`char-counter mt-2 ${
              isOverLimit ? "text-danger" : "text-muted"
            }`}
          >
            {charCount}/{maxLength} characters
          </div>
        )}

        {showError && error && hasInteracted && (
          <div className="invalid-feedback d-block">{error}</div>
        )}
      </div>
    );
  }
);

TinyMCEEditorField.displayName = "TinyMCEEditorField";
